import { useMemo } from 'react';
import { PRICE_TYPE_LOGICS, ProductTarifType, SERVICE_TYPES, TARIF_TYPE } from 'shared/constants/business.constants';
import {
  CatalogPriceZoneServiceProvider,
  CatalogPriceZoneSP,
  ManualProductPrices,
  QueryParams,
  UrbanCenterWithZone,
} from 'shared/types';
import { catalogPriceService, priceFamilyService, productService } from 'features/products/services';
import { serviceProviderService } from 'features/service-providers/services';
import { DocumentProductLinePrice } from 'features/documents/models';
import { Price, PriceFamily, PriceOption, Product } from 'features/products/models';
import { urbanCenterService } from 'shared/services';
import { useRequest } from 'shared/hooks';
import { PriceFamiliesParams } from '../types';
import ServiceProvider from 'features/service-providers/models/service-provider.model';
import ServiceType from 'features/service-providers/models/service-type.model';
import { mapCatalogResultsToClientLines } from './catalog-price-zone-service-provider.utils';

// Extended provider interface to handle various API response formats
interface ExtendedServiceProvider extends ServiceProvider {
  ServiceTypes?: ServiceType[];
  serviceTypes?: ServiceType[];
  Service_Types?: ServiceType[];
  service_types?: ServiceType[];
  serviceTypeKey?: string;
  serviceType?: string;
  service_type?: string;
  ServiceType?: string;
}

export type InputProductType = {
  id?: number;
  productId?: number;
  priceFamilyId?: number;
  quantity?: number;
  documentProductLineId?: number;
  DocumentProductLinePrices?: DocumentProductLinePrice[];
  unitPrice?: string | number;
  type?: ProductTarifType;
};

export type MoreProductType = {
  serviceProviderId?: number;
  productId?: number;
};

type CatalogPriceZoneServiceProviderQueryParamsType = QueryParams & {
  serviceProviderZoneIds?: UrbanCenterWithZone[] | null;
  products?: InputProductType[];
  siteAddressFull?: string;
  contactId?: number;
  latitude?: string;
  longitude?: string;
};

export const useCatalogPriceZoneServiceProviderQuery = (query?: CatalogPriceZoneServiceProviderQueryParamsType) => {
  return useMemo(() => {
    return [{ ...query }];
  }, []);
};

export type CatalogPriceZoneSPPriceOption = PriceOption & {
  priceValue: string;
  catalogPriceValue?: string;
  isManualPrice?: boolean;
  wasInitiallyManual?: boolean;
  orderCreatedAt?: string | null;
};

export type CatalogPriceZoneSPPrice = Price & {
  priceValue: string;
  catalogPriceValue?: string;
  isManualPrice?: boolean;
  wasInitiallyManual?: boolean;
  priceOptions?: CatalogPriceZoneSPPriceOption[];
  orderCreatedAt?: string | null;
};

export type CatalogPriceZoneSPPriceFamily = PriceFamily & {
  serviceProviderId: number;
  priceValue: string;
  catalogPriceValue?: string;
  comment: string;
  prices?: CatalogPriceZoneSPPrice[];
  isManualPrice?: boolean;
  wasInitiallyManual?: boolean;
  orderCreatedAt?: string | null;
};

export type CombineCatalogPriceLineProduct = {
  uuid?: string;
  originalProductId?: number;
  productId?: number;
  productName?: string;
  priceFamilies?: CatalogPriceZoneSPPriceFamily[];
  priceValue: string | number;
  catalogPriceValue?: string;
  isManualPrice?: boolean;
  wasInitiallyManual?: boolean;
  selectedPriceFamilyId?: number | null;
  quantity?: number;
  comment?: string | null;
  isTransport?: boolean;
  productTypeInterventionKey?: string;
  priceMargin?: string;
  sellingUnitPrice?: number;
  documentProductLineId?: number;
  type?: ProductTarifType;
  clientOrderProductLineIds?: number[];
  unitPrice?: number; // Add unitPrice
  totalPrice?: number;
  isWasteTreatment?: boolean;
  serviceProviderPrice?: string | number;
  isValidQuantity?: boolean;
  orderCreatedAt?: string | null;
};
export type CombineCatalogPriceLineZoneServiceProvider = {
  serviceProviderId: number;
  serviceProviderName: string;
  serviceTypeKey?: string;
  formattedAddress: string;
  isValid?: boolean;
  products: CombineCatalogPriceLineProduct[];
  zones: {
    zoneId: number;
    zoneName: string;
    distanceByCar?: string;
  }[];
};

/**
 * Hook to fetch catalog price zone service providers based on query parameters.
 *
 * @param query - The query parameters for fetching catalog price zone service providers.
 * @returns A request object with the fetched data.
 */
export const useCatalogPriceZoneServiceProvider = (query: CatalogPriceZoneServiceProviderQueryParamsType) => {
  /**
   * Fetches catalog price zone service providers and associated data.
   *
   * @param options - Optional query parameters to merge with the original query.
   * @returns A promise resolving to an array of combined catalog price line zone service provider data.
   */
  const action = async (options?: CatalogPriceZoneServiceProviderQueryParamsType) => {
    try {
      const mergedQuery = { ...query, ...options };
      const { serviceProviderZoneIds, products: inputProducts = [] } = mergedQuery;
      // Return early if no zone IDs or inputProducts are provided
      if (!inputProducts.length || !serviceProviderZoneIds) return [];

      // Fetching to get the price for zone service providers
      const zoneIdsArr = serviceProviderZoneIds.map(({ zoneId }) => zoneId);
      const selectedProductIds = inputProducts.map((p) => ({ productId: p.productId, priceFamilyId: p.priceFamilyId }));
      let listProductTypes = null;
      if (selectedProductIds && selectedProductIds.length > 0) {
        listProductTypes = await productService.getProductTypesAsSameInterventions({
          productIds: selectedProductIds.map((p) => p.productId).join(','),
        });
      }
      const listProductTypeIds = listProductTypes ? listProductTypes : null;
      const {
        products: initialProductResults,
        serviceProviderCatalogPriceWithPrice: initialCatalogEntries,
        manualProductPrices: initialManualProductPrices = [],
      } = await catalogPriceService.getCatalogPriceZoneServiceProviders({
        serviceProviderZoneIdFulls: serviceProviderZoneIds,
        serviceProviderZoneIds: zoneIdsArr,
        products: selectedProductIds,
        ...(listProductTypeIds ? { productTypeIds: listProductTypeIds } : {}),
        ...(mergedQuery.siteAddressFull ? { siteAddressFull: mergedQuery.siteAddressFull } : {}),
        ...(mergedQuery.contactId ? { contactId: mergedQuery.contactId } : {}),
      });

      // Create sets to track unique productIds, zoneIds, and serviceProviderIds
      const productIds = new Set<number>();
      const zoneIds = new Set<number>();
      const serviceProviderIds = new Set<number>();

      // Add productIds from productMap array
      if (initialProductResults && initialProductResults.length > 0) {
        initialProductResults.forEach((p) => {
          if (p.id != null) productIds.add(p.id);
        });
      }

      // Add productIds from inputProducts
      if (inputProducts && inputProducts.length > 0) {
        inputProducts.forEach((p) => {
          if (p.productId != null && !productIds.has(p.productId)) productIds.add(p.productId);
        });
      }

      let productResults = [...initialProductResults];
      let catalogEntries = [...initialCatalogEntries];
      let manualProductPrices = [...initialManualProductPrices];

      // Extract latitude and longitude from mergedQuery
      const { latitude, longitude } = mergedQuery;

      // Fetch missing address/zone/distance for manual price Service Providers
      const manualOnlyProviderIds = new Set<number>();

      // Identify providers that are ONLY in manual prices (not in catalogEntries)
      const catalogSPIds = new Set(
        catalogEntries.map((c) => c.serviceProviderId).filter((id): id is number => id != null),
      );

      manualProductPrices.forEach((mp) => {
        if (mp.serviceProviderId && !catalogSPIds.has(mp.serviceProviderId)) {
          manualOnlyProviderIds.add(mp.serviceProviderId);
        }
      });

      const manualOnlyProviderIdsArray = Array.from(manualOnlyProviderIds);
      const manualProviderInfoMap = new Map<
        number,
        {
          distanceByCar?: string;
          zoneName?: string;
          urbanCenterName?: string;
          zoneId?: number;
        }
      >();

      // Fetch closest urban center info for these providers if we have coordinates
      if (manualOnlyProviderIdsArray.length > 0 && latitude && longitude) {
        await Promise.all(
          manualOnlyProviderIdsArray.map(async (spId) => {
            try {
              const result = await urbanCenterService.getClosestSPUrbanCenter(spId, latitude, longitude);
              if (result) {
                manualProviderInfoMap.set(spId, {
                  distanceByCar: result.distanceByCar ? result.distanceByCar.toString() : undefined,
                  zoneName: 'Hors Zone',
                  urbanCenterName: result.name,
                });
              }
            } catch (err) {
              console.error(`Failed to fetch closest urban center for SP ${spId}`, err);
            }
          }),
        );
      }

      // Add zoneIds and serviceProviderIds from catalog entries
      catalogEntries.forEach(({ zoneId, serviceProviderId }) => {
        if (zoneId != null) zoneIds.add(Number(zoneId));
        if (serviceProviderId != null) serviceProviderIds.add(Number(serviceProviderId));
      });

      // Add serviceProviderIds from manualProductPrices
      manualProductPrices.forEach(({ serviceProviderId }) => {
        if (serviceProviderId != null) serviceProviderIds.add(Number(serviceProviderId));
      });

      // Fetch data for products, zones, and service providers
      const [initialProductsData, providersData] = await Promise.all([
        productService.getProductsByIds({
          'id[]': JSON.stringify([...productIds]),
          limit: 'unlimited',
          include: 'ProductType.ProductTypeIntervention',
        }),
        serviceProviderService.getServiceProvidersByIds({
          'id[]': JSON.stringify([...serviceProviderIds]),
          listServiceTypes: [SERVICE_TYPES.benneur, SERVICE_TYPES.camionneur].join(','),
          limit: 'unlimited',
          include: 'ServiceTypes',
        }),
      ]);

      // Extract volumes from initialProductsData and inputProducts (using initialProductsData for name)
      const volumes = [
        ...initialProductsData.rows
          .filter((product) => product.name && / - .+$/.test(product.name))
          .map((product) => product.name.match(/ - (.+)$/)?.[1] || ''),
        ...inputProducts
          .map((p) => {
            const product = initialProductsData.rows.find((prod) => prod.id === p.productId);
            return product?.name?.match(/ - (.+)$/)?.[1] || '';
          })
          .filter((volume) => volume),
      ].filter((volume) => volume);

      // Generate product names for all products (Pose, Enlèvement, and Rotation)
      const productNames = [
        ...volumes.map((volume) => `Transport Benne : Pose - ${volume}`),
        ...volumes.flatMap((volume) => [
          `Transport Benne : Enlèvement - ${volume}`,
          `Transport Benne : Rotation - ${volume}`,
        ]),
        'Location Benne : Mensuelle',
      ].filter((name): name is string => !!name);

      let productsData = {
        rows: [...(initialProductsData.rows || [])],
      };

      if (productNames.length > 0) {
        // Fetch all products in a single API call
        const productsDataNew = await productService.getProducts({
          'name[]': JSON.stringify(productNames),
          include: 'ProductType.ProductTypeUnit|ProductType.ProductTypeIntervention',
          limit: 'unlimited',
        });
        productsData = {
          rows: [...(productsData.rows || []), ...(productsDataNew.rows || [])],
        };

        // Fetch price for new products
        const newProductIds = productsDataNew.rows.map((p) => ({ productId: p.id }));
        const {
          products: newProductResults,
          serviceProviderCatalogPriceWithPrice: newCatalogEntries,
          manualProductPrices: newManualProductPrices = [],
        } = await catalogPriceService.getCatalogPriceZoneServiceProviders({
          serviceProviderZoneIdFulls: serviceProviderZoneIds,
          serviceProviderZoneIds: zoneIdsArr,
          products: newProductIds,
          ...(listProductTypeIds ? { productTypeId: listProductTypeIds } : {}),
          ...(mergedQuery.siteAddressFull ? { siteAddressFull: mergedQuery.siteAddressFull } : {}),
          ...(mergedQuery.contactId ? { contactId: mergedQuery.contactId } : {}),
        });
        console.log('newProductResults: ', newProductResults);
        productResults = [...productResults];
        catalogEntries = [...catalogEntries, ...newCatalogEntries];
        manualProductPrices = [...manualProductPrices, ...newManualProductPrices];
      }

      // Add productIds from productsData
      if (productsData.rows && productsData.rows.length > 0) {
        productsData.rows.forEach((p) => {
          if (p.id != null) productIds.add(p.id);
        });
      }

      // Fetch price families
      const productTypeIds = productsData?.rows.map((p) => p.productTypeId);
      const priceFamiliesParams: PriceFamiliesParams = {
        'productTypeId[]': JSON.stringify(productTypeIds),
        include: 'Prices.PriceOptions|Prices.PriceTypeLogic',
      };

      // Add additional query parameters if they exist
      if (mergedQuery) {
        if (mergedQuery.contactId) priceFamiliesParams.contactId = mergedQuery.contactId;
        if (mergedQuery.siteAddressFull) priceFamiliesParams.siteAddressFull = mergedQuery.siteAddressFull;
        if (mergedQuery.serviceProviderId) priceFamiliesParams.serviceProviderId = mergedQuery.serviceProviderId;

        // Use the first product ID from the products array if available
        const firstProduct = productsData?.rows?.[0];
        if (firstProduct?.id) priceFamiliesParams.productId = firstProduct.id;
      }

      const priceFamiliesData = await priceFamilyService.getPriceFamilies(priceFamiliesParams);
      const productMap = mapCatalogResultsToClientLines({
        inputProducts,
        productResults,
        priceFamilies: priceFamiliesData,
      });

      // Preparing the quantity number list for source products by ordering
      const qtyListForProducts = inputProducts
        .map((p) => {
          const priceFamily = priceFamiliesData?.find((pf) => pf.id === p?.priceFamilyId);
          const product = productMap?.find((prod) => prod.productId === p?.productId);
          // If the price family is semiforfait and the product type is not semiforfait non transform
          if (priceFamily?.key === TARIF_TYPE.SEMIFORFAIT && product?.type !== TARIF_TYPE.SEMIFORFAIT_NON_TRANSFORM) {
            // Initialize the quantity number list
            let qtyList: number[] = [parseFloat(p?.quantity?.toString() ?? '0') ?? 0];
            // Get the document product line price option list
            const documentProductLinePriceOptionList = p?.DocumentProductLinePrices?.filter(
              (price) => price?.priceId && price?.priceOptionId,
            );
            if (documentProductLinePriceOptionList && documentProductLinePriceOptionList.length > 0) {
              // For each document product line price option
              documentProductLinePriceOptionList.forEach((documentProductLinePriceOption) => {
                const price = priceFamily?.Prices?.find((p) => p.id === documentProductLinePriceOption?.priceId);
                // If the price type logic is calculate
                if (price?.PriceTypeLogic?.key === PRICE_TYPE_LOGICS?.calculate) {
                  const qtyNumber = parseFloat(documentProductLinePriceOption?.value?.toString() ?? '0');
                  // Add the quantity number to the list
                  qtyList = [...qtyList, qtyNumber];
                } else {
                  // If the price type logic is not calculate
                  qtyList = [...qtyList, 0];
                }
              });
              return qtyList;
            }
          }
          // If the price family is not semiforfait
          return [parseFloat(p?.quantity?.toString() ?? '0') ?? 0];
        })
        .flat();

      // Create maps for providers for easy access (only include active service providers)
      const providerMap = new Map(providersData?.rows.filter((sp) => sp.isActive !== false).map((sp) => [sp.id, sp]));

      // Group catalog entries
      const groupedArray: {
        serviceProviderId: number;
        group: { zoneIds: Set<number> };
      }[] = [];

      // Create a map to group catalog entries
      const groupedMap = new Map<number, { zoneIds: Set<number> }>();

      // Group entries

      // 1. Add groups from manualProductPrices FIRST (only those that are NOT in catalogEntries)
      for (const { serviceProviderId } of manualProductPrices) {
        // Skip if serviceProviderId is null
        if (serviceProviderId == null) continue;
        // Only add if it's a manual-only provider (not in catalogEntries)
        if (!manualOnlyProviderIds.has(serviceProviderId)) continue;
        if (!providerMap.get(serviceProviderId)) {
          continue;
        }

        // Create group if it doesn't exist
        let group = groupedMap.get(serviceProviderId);
        if (!group) {
          group = { zoneIds: new Set<number>() };
          groupedMap.set(serviceProviderId, group);
          groupedArray.push({ serviceProviderId, group });
        }
      }

      // 2. Add/Update groups from catalogEntries
      for (const { serviceProviderId, zoneId } of catalogEntries) {
        // Skip if serviceProviderId is null
        if (serviceProviderId == null) continue;
        if (!providerMap.get(serviceProviderId)) {
          continue;
        } // pup

        // Create group if it doesn't exist
        let group = groupedMap.get(serviceProviderId);
        // Create group
        if (!group) {
          group = { zoneIds: new Set<number>() };
          groupedMap.set(serviceProviderId, group);
          groupedArray.push({ serviceProviderId, group });
        }

        // Add zoneId to group
        if (zoneId != null) group.zoneIds.add(zoneId);
      }

      // Get selected products and their selling prices
      const selectedProductsFromOrderClient = getSelectedProductsAndSellingPricesFromOrderClient({
        inputProducts,
        productMap,
        productsData: productsData.rows,
        priceFamiliesData,
      });
      // Construct the final result array
      const result: CombineCatalogPriceLineZoneServiceProvider[] = await Promise.all(
        groupedArray.map(async ({ serviceProviderId: spId, group }) => {
          const { zoneIds } = group;
          const provider = providerMap.get(spId);

          const serviceProviderName = provider?.name || '';
          const catalogPriceForSP = catalogEntries.find((sp) => Number(sp.serviceProviderId) === spId);
          const productsList = generateProducts({
            productMap,
            productsData: productsData,
            priceFamiliesData,
            selectedProductsFromOrderClient,
            qtyListForProducts,
            inputProducts,
            catalogEntries,
            spId,
            manualProductPrices,
          });

          // Map zones with their names
          const zones = [...zoneIds].map((id) => {
            const catalogPriceZone = catalogEntries.find((sp) => sp.zoneId === id);
            return {
              zoneId: id,
              zoneName: catalogPriceZone?.zoneName || '',
              distanceByCar: catalogPriceZone?.distanceByCar || '',
            };
          });
          // Return the final result
          return {
            serviceProviderId: spId,
            serviceProviderName,
            serviceTypeKey: (() => {
              const sp = provider as ExtendedServiceProvider;
              const serviceTypes = sp?.ServiceTypes || sp?.serviceTypes || sp?.Service_Types || sp?.service_types;
              if (Array.isArray(serviceTypes) && serviceTypes.length > 0) {
                const camionneur = serviceTypes.find((st) => st?.key?.toLowerCase() === SERVICE_TYPES.camionneur);
                if (camionneur) return SERVICE_TYPES.camionneur;

                const benneur = serviceTypes.find((st) => st?.key?.toLowerCase() === SERVICE_TYPES.benneur);
                if (benneur) return SERVICE_TYPES.benneur;

                return serviceTypes[0]?.key;
              }

              return sp?.serviceTypeKey || sp?.serviceType || sp?.service_type || sp?.ServiceType || undefined;
            })(),
            isValid: catalogPriceForSP?.isValid,
            formattedAddress: catalogPriceForSP?.formattedAddress || provider?.formattedAddress || '',
            products: productsList,
            urbanCenterName: catalogPriceForSP?.urbanCenterName || manualProviderInfoMap.get(spId)?.urbanCenterName,
            zones:
              zones.length > 0
                ? zones
                : manualOnlyProviderIds.has(spId)
                  ? [
                      {
                        zoneId: 0, // Dummy ID
                        zoneName: manualProviderInfoMap.get(spId)?.zoneName || 'Hors Zone',
                        distanceByCar: manualProviderInfoMap.get(spId)?.distanceByCar,
                      },
                    ]
                  : [],
          };
        }),
      );

      // if the result is empty, generate the products for the manual selected Service Provider with invalid spId
      const products =
        result?.[0]?.products ||
        generateProducts({
          productMap,
          productsData: productsData,
          priceFamiliesData,
          selectedProductsFromOrderClient,
          qtyListForProducts,
          inputProducts,
          catalogEntries,
          spId: 0,
          manualProductPrices,
        });
      return {
        catalogPriceResult: result,
        products,
        selectedProducts: selectedProductsFromOrderClient,
        manualProductPrices,
      };
    } catch (error) {
      console.error('useCatalogPriceZoneServiceProvider error:', error);
      return [];
    }
  };

  /**
   * Generates the list of products for the Service Provider
   * @param params - The parameters for generating products
   * @returns The generated products
   */
  const generateProducts = ({
    productMap,
    productsData,
    priceFamiliesData,
    selectedProductsFromOrderClient,
    qtyListForProducts,
    inputProducts,
    catalogEntries,
    spId,
    manualProductPrices,
  }: {
    productMap: InputProductType[];
    productsData: { rows: Product[] };
    priceFamiliesData: PriceFamily[];
    selectedProductsFromOrderClient: SelectedProductsAndSellingPrices[];
    qtyListForProducts: number[];
    inputProducts: InputProductType[];
    catalogEntries: CatalogPriceZoneServiceProvider[];
    spId: number;
    manualProductPrices: ManualProductPrices[];
  }) => {
    // Map inputProducts to productResults
    let productsList = productMap.map((p, index) => {
      // Find the product
      const product = productsData?.rows.find((prod) => prod.id === p.productId);

      // Find the catalog price for the product based on properly price type
      const groupedByPriceType = catalogEntries.reduce(
        (acc, entry) => {
          const key = `${entry.serviceProviderId}_${entry.priceType}`;
          if (!acc[key]) {
            acc[key] = {
              ...entry,
              general: [...(entry.general || [])],
              forfait: [...(entry.forfait || [])],
              semiforfait: [...(entry.semiforfait || [])],
            };
          } else {
            acc[key]?.general?.push(...(entry.general || []));
            acc[key]?.forfait?.push(...(entry.forfait || []));
            acc[key]?.semiforfait?.push(...(entry.semiforfait || []));
          }
          return acc;
        },
        {} as Record<string, CatalogPriceZoneServiceProvider>,
      );
      const groupedValues = Object.values(groupedByPriceType) as CatalogPriceZoneServiceProvider[];
      const catalogPriceForSPByPriceType = groupedValues.find(
        (sp) => Number(sp.serviceProviderId) === spId && sp.priceType === p.type,
      );

      // Get the correct tarif types
      const correctTarifList = getCorrectDataTarifTypes(p.type, catalogPriceForSPByPriceType);

      // Handles for the case - no tarif details
      const catalogPriceForProduct = correctTarifList.find(
        (e) => e.productId === product?.id && Number(e.serviceProviderId) === spId,
      );
      // Get the price value and comment
      let orderCreatedAt = catalogPriceForProduct?.orderCreatedAt;
      const catalogIsManual = catalogPriceForProduct?.isManualPrice === true;
      let catalogPriceValue = !catalogIsManual ? (catalogPriceForProduct?.priceValue ?? '') : '';
      let priceValue = catalogPriceValue;
      let comment = catalogPriceForProduct?.comment ?? '';
      let isManualPrice = false;
      let wasInitiallyManual = false;

      // Check for manual price in database
      const manualPrice = manualProductPrices.find(
        (manual) => manual.productId === p.productId && manual.serviceProviderId === spId,
      );

      if (manualPrice) {
        // If manual price exists, set the flags immediately
        isManualPrice = true;
        wasInitiallyManual = true;
        if (manualPrice.updatedAt) {
          orderCreatedAt = new Date(manualPrice.updatedAt).toISOString();
        }
      } else if (!orderCreatedAt && catalogPriceForProduct?.orderCreatedAt) {
        orderCreatedAt = catalogPriceForProduct.orderCreatedAt;
      }

      // If no catalog price found, try manualProductPrices
      if (!priceValue || priceValue === '') {
        const manualPrice = manualProductPrices.find(
          (manual) => manual.productId === p.productId && manual.serviceProviderId === spId,
        );
        if (manualPrice?.price) {
          priceValue = manualPrice.price;
          isManualPrice = true;
          wasInitiallyManual = true;
          if (manualPrice.updatedAt) {
            orderCreatedAt = new Date(manualPrice.updatedAt).toISOString();
          }
        }
      }

      // Handles for the case - tarif details
      const selectedPriceFamilyId = p?.priceFamilyId;
      // Find the price families
      const filteredPriceFamilies = priceFamiliesData.filter((pf) => pf.productTypeId === product?.productTypeId);

      // Map product with its price families
      const priceFamilies = mappingCatalogPriceZoneSPForPriceFamilies({
        serviceProviderId: spId,
        productId: p.productId as number,
        priceFamilies: filteredPriceFamilies,
        catalogPriceZoneSP: catalogPriceForSPByPriceType,
        manualProductPrices: manualProductPrices,
      });

      // Find the selected price family
      const selectedPriceFamily = priceFamilies.find((pf) => pf.id === selectedPriceFamilyId);
      if (selectedPriceFamily) {
        // Get the price value and comment
        priceValue = selectedPriceFamily?.priceValue ?? '';
        catalogPriceValue = selectedPriceFamily?.catalogPriceValue ?? '';
        comment = selectedPriceFamily?.comment ?? '';

        // If priceValue is empty, try to get from manualProductPrices
        if (!priceValue || priceValue === '') {
          const manualPrice = manualProductPrices.find(
            (manual) =>
              manual.productId === p.productId &&
              manual.serviceProviderId &&
              manual.priceFamilyId === selectedPriceFamilyId,
          );
          if (manualPrice) {
            priceValue = manualPrice.price;
          }
        }
      } else {
        const manualPrice = manualProductPrices.find(
          (manual) =>
            manual.productId === p.productId &&
            manual.serviceProviderId === spId &&
            manual.serviceProviderId &&
            manual.priceFamilyId === selectedPriceFamilyId,
        );
        if (manualPrice) {
          priceValue = manualPrice?.price ?? '';
          isManualPrice = true;
          wasInitiallyManual = true;
        }
      }
      // Find the corresponding selected product from selectedProductsFromOrderClient
      const selectedProduct = selectedProductsFromOrderClient.find(
        (sp) => sp.productId === p.productId && sp.documentProductLineId === p.documentProductLineId,
      );
      const serviceProviderPrice = priceValue;
      // Map product with its price families
      return {
        productId: product?.id,
        productName: product?.name ?? '',
        priceFamilies,
        priceValue,
        catalogPriceValue,
        isManualPrice,
        wasInitiallyManual,
        selectedPriceFamilyId,
        quantity: product?.isTransport ? 1 : qtyListForProducts[index],
        isTransport: product?.isTransport,
        comment,
        productTypeInterventionKey: product?.ProductType?.ProductTypeIntervention?.key,
        sellingUnitPrice:
          selectedProduct?.sellingUnitPrice ?? parseFloat(inputProducts[index]?.unitPrice?.toString() ?? '0'),
        isWasteTreatment: product?.isWasteTreatment ?? false,
        serviceProviderPrice,
        orderCreatedAt,
        documentProductLineId: p.documentProductLineId,
        type: p.type,
      } as CombineCatalogPriceLineProduct;
    });
    // Filter and add relevant products based on quantity
    const additionalProducts: typeof productsList = [];
    productsList.forEach((productItem, index) => {
      const quantity = inputProducts[index]?.quantity ?? 1;
      const volumeMatch = productItem.productName?.match(/ - (.+)$/)?.[1] || ''; // Extract volume (e.g., '10m3')
      // const inputUnitPrice = parseFloat(inputProducts[index]?.unitPrice?.toString() ?? '0');

      const sourceSelectedProduct = selectedProductsFromOrderClient.find(
        (sp) =>
          sp.productId === productItem.productId &&
          sp.documentProductLineId === productMap[index]?.documentProductLineId,
      );
      // Handle 'Pose et enlèvement' products
      if (productItem.productName && /Transport Benne : Pose et enlèvement - .+/.test(productItem.productName)) {
        // Add Enlèvement product
        const enlevementProduct = productsData.rows.find(
          (prod) => prod.name === `Transport Benne : Enlèvement - ${volumeMatch}`,
        );
        const productCatalogPrices = catalogEntries
          .filter((sp) => Number(sp.serviceProviderId) === spId && sp.priceType === 'GENERAL')
          .find(
            (generalCatalogPrice) =>
              generalCatalogPrice?.general &&
              generalCatalogPrice?.general?.find(
                (general: CatalogPriceZoneSP) => general.productId === enlevementProduct?.id,
              ),
          );
        const enlevementCatalogPrice = productCatalogPrices?.general?.find(
          (general: CatalogPriceZoneSP) => general.productId === enlevementProduct?.id,
        );
        if (enlevementProduct) {
          // Only set price from catalog if it's not a manual price
          const enlevementCatalogIsManual = enlevementCatalogPrice?.isManualPrice === true;
          let enlevementPriceValue = !enlevementCatalogIsManual ? (enlevementCatalogPrice?.priceValue ?? '') : '';
          let orderCreatedAt = enlevementCatalogPrice?.orderCreatedAt;
          let enlevementIsManualPrice = false;
          let wasInitiallyManual = false;

          // Check for manual price in database
          const manualPrice = manualProductPrices.find(
            (manual) => manual.productId === enlevementProduct.id && manual.serviceProviderId === spId,
          );

          if (manualPrice) {
            enlevementIsManualPrice = true;
            wasInitiallyManual = true;
            if (manualPrice.updatedAt) {
              orderCreatedAt = new Date(manualPrice.updatedAt).toISOString();
            }
          } else if (!orderCreatedAt) {
            orderCreatedAt = enlevementCatalogPrice?.orderCreatedAt;
          }

          // Store catalogPriceValue (original catalog price before manual override)
          const enlevementCatalogPriceValue = !enlevementCatalogIsManual
            ? (enlevementCatalogPrice?.priceValue ?? '')
            : '';

          // If no catalog price found, try manualProductPrices for price value
          if (!enlevementPriceValue) {
            if (manualPrice?.price) {
              enlevementPriceValue = manualPrice.price;
            }
          }

          additionalProducts.push({
            productId: enlevementProduct.id,
            productName: enlevementProduct.name,
            priceValue: enlevementPriceValue,
            catalogPriceValue: enlevementCatalogPriceValue,
            comment: enlevementCatalogPrice?.comment ?? '',
            priceFamilies: [],
            selectedPriceFamilyId: undefined,
            quantity: 0,
            isTransport: enlevementProduct.isTransport,
            productTypeInterventionKey: enlevementProduct?.ProductType?.ProductTypeIntervention?.key,
            sellingUnitPrice: sourceSelectedProduct?.sellingUnitPrice ?? 0,
            isWasteTreatment: false,
            serviceProviderPrice: enlevementCatalogPrice?.priceValue ?? '',
            orderCreatedAt,
            isManualPrice: enlevementIsManualPrice,
            wasInitiallyManual,
          });
        }

        // Add rotation for Pose et enlèvement if quantity > 1
        if (quantity > 1) {
          const rotationProduct = productsData.rows.find(
            (prod) => prod.name === `Transport Benne : Rotation - ${volumeMatch}`,
          );
          const productCatalogPrices = catalogEntries
            .filter((sp) => Number(sp.serviceProviderId) === spId && sp.priceType === 'GENERAL')
            .find(
              (generalCatalogPrice) =>
                generalCatalogPrice?.general &&
                generalCatalogPrice?.general?.find(
                  (general: CatalogPriceZoneSP) => general.productId === rotationProduct?.id,
                ),
            );
          const rotationCatalogPrice = productCatalogPrices?.general?.find(
            (general: CatalogPriceZoneSP) => general.productId === rotationProduct?.id,
          );
          if (rotationProduct) {
            // Only set price from catalog if it's not a manual price
            const rotationCatalogIsManual = rotationCatalogPrice?.isManualPrice === true;
            let rotationPriceValue = !rotationCatalogIsManual ? (rotationCatalogPrice?.priceValue ?? '') : '';
            let orderCreatedAt = rotationCatalogPrice?.orderCreatedAt;
            let rotationIsManualPrice = false;
            let wasInitiallyManual = false;

            // Check for manual price in database
            const manualPrice = manualProductPrices.find(
              (manual) => manual.productId === rotationProduct.id && manual.serviceProviderId === spId,
            );

            if (manualPrice) {
              rotationIsManualPrice = true;
              wasInitiallyManual = true;
              if (manualPrice.updatedAt) {
                orderCreatedAt = new Date(manualPrice.updatedAt).toISOString();
              }
            } else if (!orderCreatedAt) {
              orderCreatedAt = rotationCatalogPrice?.orderCreatedAt;
            }

            // Store catalogPriceValue (original catalog price before manual override)
            const rotationCatalogPriceValue = !rotationCatalogIsManual ? (rotationCatalogPrice?.priceValue ?? '') : '';

            // If no catalog price found, try manualProductPrices for price value
            if (!rotationPriceValue) {
              if (manualPrice?.price) {
                rotationPriceValue = manualPrice.price;
              }
            }

            additionalProducts.push({
              productId: rotationProduct.id,
              productName: rotationProduct.name,
              priceValue: rotationPriceValue,
              catalogPriceValue: rotationCatalogPriceValue,
              comment: rotationCatalogPrice?.comment ?? '',
              quantity: 0,
              priceFamilies: [],
              selectedPriceFamilyId: undefined,
              isTransport: rotationProduct.isTransport,
              productTypeInterventionKey: rotationProduct?.ProductType?.ProductTypeIntervention?.key,
              sellingUnitPrice: sourceSelectedProduct?.sellingUnitPrice ?? 0,
              isWasteTreatment: false,
              serviceProviderPrice: rotationCatalogPrice?.priceValue ?? '',
              orderCreatedAt,
              isManualPrice: rotationIsManualPrice,
              wasInitiallyManual,
            });
          }
        }
      }
    });

    // Append additional products to the end of the list
    productsList = [
      ...productsList.map((productItem) => {
        if (productItem.productName && /Transport Benne : Pose et enlèvement - .+/.test(productItem.productName)) {
          const volume = productItem.productName.match(/Transport Benne : Pose et enlèvement - (.+)/)?.[1] || '';
          const poseProduct = productsData.rows.find((prod) => prod.name === `Transport Benne : Pose - ${volume}`);
          const productCatalogPrices = catalogEntries
            .filter((sp) => Number(sp.serviceProviderId) === spId && sp.priceType === 'GENERAL')
            .find(
              (generalCatalogPrice) =>
                generalCatalogPrice?.general &&
                generalCatalogPrice?.general?.find(
                  (general: CatalogPriceZoneSP) => general.productId === poseProduct?.id,
                ),
            );
          const poseProductCatalogPrice = productCatalogPrices?.general?.find(
            (general: CatalogPriceZoneSP) => general.productId === poseProduct?.id,
          );

          // Find the corresponding selected product
          const selectedProduct = selectedProductsFromOrderClient.find((sp) =>
            sp.productName.includes(`Pose et enlèvement - ${volume}`),
          );

          // Only set price from catalog if it's not a manual price
          const poseCatalogIsManual = poseProductCatalogPrice?.isManualPrice === true;
          let posePriceValue = !poseCatalogIsManual ? (poseProductCatalogPrice?.priceValue ?? '') : '';
          let orderCreatedAt = poseProductCatalogPrice?.orderCreatedAt;
          let poseIsManualPrice = false;
          let wasInitiallyManual = false;

          // Check for manual price in database
          const manualPrice = manualProductPrices.find(
            (manual) => manual.productId === poseProduct?.id && manual.serviceProviderId === spId,
          );

          if (manualPrice) {
            poseIsManualPrice = true;
            wasInitiallyManual = true;
            if (manualPrice.updatedAt) {
              orderCreatedAt = new Date(manualPrice.updatedAt).toISOString();
            }
          } else if (!orderCreatedAt) {
            orderCreatedAt = poseProductCatalogPrice?.orderCreatedAt;
          }

          // Store catalogPriceValue (original catalog price before manual override)
          const poseCatalogPriceValue = !poseCatalogIsManual ? (poseProductCatalogPrice?.priceValue ?? '') : '';

          // If no catalog price found, try manualProductPrices for price value
          if (!posePriceValue) {
            if (manualPrice?.price) {
              posePriceValue = manualPrice.price;
            }
          }

          return {
            ...productItem,
            productId: poseProduct?.id ?? productItem.productId,
            productName: `Transport Benne : Pose - ${volume}`,
            priceValue: posePriceValue,
            catalogPriceValue: poseCatalogPriceValue,
            comment: poseProductCatalogPrice?.comment ?? '',
            quantity: 1,
            isTransport: poseProduct?.isTransport ?? productItem.isTransport,
            sellingUnitPrice: selectedProduct?.sellingUnitPrice ?? productItem.sellingUnitPrice,
            orderCreatedAt,
            isManualPrice: poseIsManualPrice,
            wasInitiallyManual,
          };
        }
        if (productItem.productName === 'Location Benne : Journalier') {
          const mensuelleProduct = productsData.rows.find((prod) => prod.name === `Location Benne : Mensuelle`);
          const productCatalogPrices = catalogEntries
            .filter((sp) => Number(sp.serviceProviderId) === spId && sp.priceType === 'GENERAL')
            .find(
              (generalCatalogPrice) =>
                generalCatalogPrice?.general &&
                generalCatalogPrice?.general?.find(
                  (general: CatalogPriceZoneSP) => general.productId === mensuelleProduct?.id,
                ),
            );
          const mensuelleProductCatalogPrice = productCatalogPrices?.general?.find(
            (general: CatalogPriceZoneSP) => general.productId === mensuelleProduct?.id,
          );
          // Find the corresponding selected product
          const selectedProduct = selectedProductsFromOrderClient.find((sp) =>
            sp.productName.includes(`Location Benne : Journalier`),
          );

          // Only set price from catalog if it's not a manual price
          const mensuelleCatalogIsManual = mensuelleProductCatalogPrice?.isManualPrice === true;
          let mensuellePriceValue = !mensuelleCatalogIsManual ? (mensuelleProductCatalogPrice?.priceValue ?? '') : '';
          let orderCreatedAt = mensuelleProductCatalogPrice?.orderCreatedAt;
          let mensuelleIsManualPrice = false;
          let wasInitiallyManual = false;

          // Check for manual price in database
          const manualPrice = manualProductPrices.find(
            (manual) => manual.productId === mensuelleProduct?.id && manual.serviceProviderId === spId,
          );

          if (manualPrice) {
            mensuelleIsManualPrice = true;
            wasInitiallyManual = true;
            if (manualPrice.updatedAt) {
              orderCreatedAt = new Date(manualPrice.updatedAt).toISOString();
            }
          } else if (!orderCreatedAt) {
            orderCreatedAt = mensuelleProductCatalogPrice?.orderCreatedAt;
          }

          // Store catalogPriceValue (original catalog price before manual override)
          const mensuelleCatalogPriceValue = !mensuelleCatalogIsManual
            ? (mensuelleProductCatalogPrice?.priceValue ?? '')
            : '';

          // If no catalog price found, try manualProductPrices for price value
          if (!mensuellePriceValue) {
            if (manualPrice?.price) {
              mensuellePriceValue = manualPrice.price;
            }
          }

          return {
            ...productItem,
            originalProductId: productItem.productId,
            productId: mensuelleProduct?.id ?? productItem.productId,
            productName: `Location Benne : Mensuelle`,
            priceValue: mensuellePriceValue,
            catalogPriceValue: mensuelleCatalogPriceValue,
            comment: mensuelleProductCatalogPrice?.comment ?? '',
            quantity: productItem.quantity ?? 0,
            isTransport: mensuelleProduct?.isTransport ?? productItem.isTransport,
            sellingUnitPrice: ((selectedProduct?.sellingUnitPrice ?? productItem.sellingUnitPrice) || 0) * 30,
            orderCreatedAt,
            isManualPrice: mensuelleIsManualPrice,
            wasInitiallyManual,
          };
        }
        return productItem;
      }),
      ...additionalProducts,
    ];
    // Return the final result
    return productsList;
  };

  return useRequest<{
    catalogPriceResult: CombineCatalogPriceLineZoneServiceProvider[];
    products: CombineCatalogPriceLineProduct[];
    selectedProducts: SelectedProductsAndSellingPrices[];
    manualProductPrices: ManualProductPrices[];
  }>({
    action,
    params: query,
    default: [],
  });
};

export type CatalogPriceZonesSPType = {
  general?: CatalogPriceZoneSP[];
  forfait?: CatalogPriceZoneSP[];
  semiforfait?: CatalogPriceZoneSP[];
} | null;

/**
 * Map the catalog price zone service providers for price families.
 * @param params - The parameters of the mapping.
 * @param params.serviceProviderId - The service provider ID.
 * @param params.productId - The product ID.
 * @param params.priceFamilies - The list of price families.
 * @param params.catalogPriceZoneSP - The list of catalog price zone service providers.
 * @param params.manualProductPrices - The list of manual product prices for fallback.
 * @returns The list of price families mapped with their prices.
 */
export const mappingCatalogPriceZoneSPForPriceFamilies = (params: {
  serviceProviderId: number;
  productId: number;
  priceFamilies: PriceFamily[];
  catalogPriceZoneSP?: CatalogPriceZonesSPType;
  manualProductPrices?: ManualProductPrices[];
}): CatalogPriceZoneSPPriceFamily[] => {
  // Destructure params
  const { serviceProviderId, productId, priceFamilies, catalogPriceZoneSP, manualProductPrices = [] } = params;

  // Map price families
  return priceFamilies.map((pf) => {
    const matchingPriceFamily = pf;

    // Get the correct tarif types based on the price type
    const correctTarifList = getCorrectDataTarifTypes(matchingPriceFamily.key, catalogPriceZoneSP);

    // find the catalog entry for the matching price family
    const catalogEntryForPriceFamily = correctTarifList.find(
      (e) =>
        (e.productId === productId || e.destProductId === productId) &&
        Number(e.serviceProviderId) === serviceProviderId &&
        ((e.priceFamilyId === matchingPriceFamily.id && e.priceId === null && e.priceOptionId === null) ||
          (e.DestCatalogPrice?.priceFamilyId === matchingPriceFamily.id &&
            e.DestCatalogPrice?.priceId === null &&
            e.DestCatalogPrice?.priceOptionId === null)),
    );

    // Map price families with their prices
    const prices = (matchingPriceFamily.Prices || []).map((price) => {
      // find the catalog entry for the matching price
      const catalogEntryForPrice = correctTarifList.find(
        (e) =>
          (e.productId === productId || e.destProductId === productId) &&
          Number(e.serviceProviderId) === serviceProviderId &&
          ((e.priceFamilyId === matchingPriceFamily.id && e.priceId === price.id && e.priceOptionId === null) ||
            (e.DestCatalogPrice?.priceFamilyId === matchingPriceFamily.id &&
              e.DestCatalogPrice?.priceId === price.id &&
              e.DestCatalogPrice?.priceOptionId === null)),
      );

      // Map price options
      const priceOptions: CatalogPriceZoneSPPriceOption[] = (price.PriceOptions || []).map((po) => {
        // find the catalog entry for the matching price
        const catalogEntryForPriceOption = correctTarifList.find(
          (e) =>
            (e.productId === productId || e.destProductId === productId) &&
            Number(e.serviceProviderId) === serviceProviderId &&
            ((e.priceFamilyId === matchingPriceFamily.id && e.priceId === price.id && e.priceOptionId === po.id) ||
              (e.DestCatalogPrice?.priceFamilyId === matchingPriceFamily.id &&
                e.DestCatalogPrice?.priceId === price.id &&
                e.DestCatalogPrice?.priceOptionId === po.id)),
        );

        const catalogPriceValue = catalogEntryForPriceOption?.priceValue ?? po?.defaultValue ?? '';
        const orderCreatedAt = catalogEntryForPriceOption?.orderCreatedAt ?? null;

        // Map price option with its value
        return {
          ...po,
          priceValue: catalogPriceValue,
          catalogPriceValue,
          orderCreatedAt,
        };
      });

      const manualPriceEntry = price.manualOptionPrices?.find(
        (mop) => mop.serviceProviderId === serviceProviderId && mop.productId === productId,
      );
      const manualPrice = manualPriceEntry?.manualPrice;
      const catalogPriceValue = catalogEntryForPrice?.priceValue ?? price?.defaultPrice?.toString() ?? '';
      const finalPriceValue = manualPrice?.toString() ?? catalogPriceValue;
      const orderCreatedAt = manualPriceEntry?.orderCreatedAt || (catalogEntryForPrice?.orderCreatedAt ?? null);

      // Map price with its value
      return {
        ...price,
        priceValue: finalPriceValue,
        catalogPriceValue,
        priceOptions,
        orderCreatedAt,
        ...(manualPrice != null && {
          isManualPrice: true,
          wasInitiallyManual: true,
        }),
      };
    });

    const catalogPriceValue = catalogEntryForPriceFamily?.priceValue ?? '';

    // Find existing manual override for the family
    const manualFamilyOverride = (manualProductPrices || []).find(
      (m) =>
        m.productId === productId &&
        m.serviceProviderId === serviceProviderId &&
        m.priceFamilyId === matchingPriceFamily.id,
    );

    const priceFamilyValue = manualFamilyOverride?.price ?? catalogPriceValue;
    const wasInitiallyManualFamily = !!manualFamilyOverride;
    const orderCreatedAt = manualFamilyOverride?.updatedAt
      ? new Date(manualFamilyOverride.updatedAt).toISOString()
      : (catalogEntryForPriceFamily?.orderCreatedAt ?? null);

    const isManualPriceFamily =
      wasInitiallyManualFamily || (catalogPriceValue !== '' && Number(priceFamilyValue) !== Number(catalogPriceValue));

    const result = {
      ...matchingPriceFamily,
      serviceProviderId,
      priceValue: priceFamilyValue,
      catalogPriceValue,
      isManualPrice: isManualPriceFamily,
      wasInitiallyManual: wasInitiallyManualFamily,
      comment: catalogEntryForPriceFamily?.comment ?? '',
      prices,
      orderCreatedAt,
    };

    return result;
  });
};

/**
 * Get the correct tarif types based on the price type.
 * @param priceType - The price type.
 * @param catalogPriceZoneSP - The catalog price zone service providers.
 * @returns The correct tarif types.
 */
export const getCorrectDataTarifTypes = (
  priceType?: ProductTarifType | null,
  catalogPriceZoneSP?: CatalogPriceZonesSPType,
): CatalogPriceZoneSP[] => {
  /**
   * Switch on the price type to get the correct tarif types.
   * - GENERAL: return the general prices.
   * - FORFAIT: return the forfait prices.
   * - SEMIFORFAIT: return the semiforfait prices.
   * - default: return an empty array.
   */
  switch (priceType) {
    case TARIF_TYPE.GENERAL:
      return catalogPriceZoneSP?.general ?? [];
    case TARIF_TYPE.FORFAIT:
      return catalogPriceZoneSP?.forfait ?? [];
    case TARIF_TYPE.SEMIFORFAIT:
    case TARIF_TYPE.SEMIFORFAIT_NON_TRANSFORM:
      return catalogPriceZoneSP?.semiforfait ?? [];
    default:
      return [];
  }
};

export type SelectedProductsAndSellingPrices = {
  productId: number;
  productName: string;
  sellingUnitPrice: number;
  documentProductLineId?: number;
  isBenne: boolean;
  isTransport: boolean;
};

/**
 * Retrieves selected products and their selling prices from an order client.
 * @param params - The input parameters including input products, product map, product data, and price family data.
 * @returns An array of objects containing productId, productName, and sellingUnitPrice.
 */
const getSelectedProductsAndSellingPricesFromOrderClient = (params: {
  inputProducts: InputProductType[];
  productMap: InputProductType[];
  productsData: Product[];
  priceFamiliesData: PriceFamily[];
}): SelectedProductsAndSellingPrices[] => {
  const { inputProducts, productMap, productsData, priceFamiliesData } = params;

  // Prepare selling unit price list corresponding to products list
  const sellingUnitPriceList = inputProducts
    .map((p) => {
      // Find the price family associated with the input product
      const priceFamily = priceFamiliesData?.find((pf) => pf.id === p?.priceFamilyId);
      // Find the product in the product map
      const product = productMap?.find((prod) => prod.productId === p?.productId);

      // Check if the price family is semiforfait and the product type is not semiforfait non transform
      if (priceFamily?.key === TARIF_TYPE.SEMIFORFAIT && product?.type !== TARIF_TYPE.SEMIFORFAIT_NON_TRANSFORM) {
        let unitPriceList: { sellingPrice: number; destSellingPrice: number }[] = [];
        // Filter the document product line prices based on conditions
        const documentProductLinePriceList = p?.DocumentProductLinePrices?.filter(
          (price) => !(price.priceId && price.priceOptionId) || (price?.priceId && !price?.priceOptionId),
        );

        // If there are valid document product line prices, calculate unit prices
        if (documentProductLinePriceList && documentProductLinePriceList.length > 0) {
          documentProductLinePriceList.forEach((documentProductLinePriceOption) => {
            // Convert the price value to a number and add it to the list
            const unitPriceNumber = parseFloat(documentProductLinePriceOption?.value?.toString() ?? '0');
            unitPriceList = [
              ...unitPriceList,
              { sellingPrice: unitPriceNumber, destSellingPrice: parseFloat(p?.unitPrice?.toString() ?? '0') ?? 0 },
            ];
          });
          return unitPriceList;
        }
      }

      // Return the unit price of the input product if no conditions are met
      return [{ sellingPrice: parseFloat(p?.unitPrice?.toString() ?? '0'), destSellingPrice: 0 }];
    })
    .flat();

  // Map each product in the product map with its corresponding selling unit price
  const mappedSellingPriceWithProducts = productMap.map((product, index) => {
    // Find the product data by product ID
    const productData = productsData?.find((prod) => prod.id === product.productId);
    const sellingUnitPrice = sellingUnitPriceList[index] ? sellingUnitPriceList[index].sellingPrice : 0;
    // If the product name contains "Transport Benne : Pose et enlèvement - "
    // if (
    //   /Transport Benne : Pose et enlèvement - .+/.test(productData?.name || '') &&
    //   !inputProducts.find((p) => p.productId === productData?.id)
    // ) {
    //   // Set the selling unit price to the unit price of the destination product
    //   sellingUnitPrice = sellingUnitPriceList[index] ? sellingUnitPriceList[index].destSellingPrice : 0;
    // }

    return {
      productId: product.productId ?? 0,
      productName: productData?.name || '',
      sellingUnitPrice,
      documentProductLineId: product.documentProductLineId,
      isBenne: productData?.ProductType?.ProductTypeIntervention?.key === 'BENNE',
      isTransport: productData?.isTransport || false,
    };
  });

  return mappedSellingPriceWithProducts;
};
